import { TFunction } from 'react-i18next'
import { DemographicAttributeEnum, DemographicStat, Player, StatTypeEnum } from 'types/global'
import { StratumDataItem } from '../types'
import StratumDataItemValue from '../stratum-data-item-value/StratumDataItemValue'
import { transformSexStratumLabel } from '../utils'

const orderMap: Record<DemographicAttributeEnum, number> = {
  [DemographicAttributeEnum.Sex]: 1,
  [DemographicAttributeEnum.Country]: 2,
  [DemographicAttributeEnum.Age]: 3,
}

const getOrder = (attribute: DemographicAttributeEnum): number => orderMap[attribute] ?? 99

export const getDataItem = (
  { strata, stratumSize, percentileBounds }: DemographicStat,
  {
    t,
    getCountryName,
  }: {
    t: TFunction<'strPlayerLevel', undefined>
    getCountryName: (countryCode: string) => string
  }
): StratumDataItem | null => {
  const firstStratum = strata?.[0]
  const percentile = percentileBounds?.[1]

  if (!firstStratum) return null

  const defaultValue = <StratumDataItemValue percentile={percentile} stratumSize={stratumSize} />

  const order = getOrder(firstStratum.attribute)

  switch (firstStratum.attribute) {
    case DemographicAttributeEnum.Country:
      return {
        order,
        label: getCountryName(firstStratum.label),
        value: defaultValue,
      }
    case DemographicAttributeEnum.Sex: {
      const sexLabel = transformSexStratumLabel(firstStratum.label)

      if (sexLabel !== 'M' && sexLabel !== 'F') return null

      return {
        order,
        label: t(`attribute.${firstStratum.attribute}.${sexLabel}`),
        value: <StratumDataItemValue percentile={percentile} stratumSize={stratumSize} />,
      }
    }
    default:
      return {
        order,
        label: t(`attribute.${firstStratum.attribute}.${firstStratum.label}`),
        value: defaultValue,
      }
  }
}

const getAgeStratumLabel = (age: number): string => {
  if (typeof age !== 'number' || isNaN(age) || age < 0) return 'Unknown'

  switch (true) {
    case age <= 12:
      return '0-12'
    case age <= 14:
      return '13-14'
    case age <= 16:
      return '15-16'
    case age <= 18:
      return '17-18'
    case age <= 30:
      return '19-30'
    case age <= 40:
      return '31-40'
    case age <= 50:
      return '41-50'
    case age <= 60:
      return '51-60'
    case age > 60:
      return '60+'
    default:
      return 'Unknown'
  }
}

export const getFallbackStats = (profile: Player): DemographicStat[] => {
  return [
    {
      type: StatTypeEnum.Percentile,
      strata: [
        {
          attribute: DemographicAttributeEnum.Sex,
          label: profile.sex || 'U',
        },
      ],
      stratumSize: 1,
      percentileBounds: [0, 0],
    },
    {
      type: StatTypeEnum.Percentile,
      strata: [
        {
          attribute: DemographicAttributeEnum.Country,
          label: profile.countryCode || 'XX',
        },
      ],
      stratumSize: 1,
      percentileBounds: [0, 0],
    },
    {
      type: StatTypeEnum.Percentile,
      strata: [
        {
          attribute: DemographicAttributeEnum.Age,
          label: getAgeStratumLabel(profile.age ?? -1),
        },
      ],
      stratumSize: 1,
      percentileBounds: [0, 0],
    },
  ]
}
